import { TimelineEvent } from "@redotech/redo-common-model-timeline-common/timeline";
import { CheckoutShippingOption } from "@redotech/redo-conversion-checkout-shipping-common/checkout-shipping-option";
import { SesEvent } from "@redotech/redo-customer-event-common/events/ses-events";
import {
  Parcel,
  ShipmentRatesOld,
} from "@redotech/redo-fulfillment-carrier-common/legacy-parcel";
import {
  Label,
  Rate,
} from "@redotech/redo-fulfillment-carrier-common/shipment";
import { Provider } from "@redotech/redo-order-common/provider";
import {
  ExternalShipmentTracker,
  ITrackable,
} from "@redotech/redo-order-tracking-trackable-common/trackable";
import {
  ExchangeOrderNameSettings,
  ExchangeOrderPrefixTypes,
} from "@redotech/redo-return-core-common/exchange";
import { MaybeOriginOrder } from "@redotech/redo-return-core-common/origin-order-utils";
import {
  InputAnswer,
  MultipleChoiceAnswer,
} from "@redotech/redo-return-core-common/return-flow-answers";
import { YofiRiskLevels } from "@redotech/redo-return-core-common/yofi-risk-level";
import { Coverage } from "@redotech/redo-return-coverage-common/coverage";
import { isRedoLineItem } from "@redotech/redo-return-coverage-common/is-redo-line-item";
import {
  OrderFulfillmentStatus,
  OrderId,
  OriginAddress,
  LineItem as ShopifyLineItem,
} from "@redotech/redo-shopify-order-common/shopify-order";
import { isDefined } from "@redotech/util/type";
import { CommerceCloudOrder } from "./commerce-cloud-order";
import { OrderRefundEvent, ProviderPrefixedOrderId } from "./order-schema";

export interface DiscountCode {
  code: string;
  amount: string;
  type: string;
}

// Keep this in sync with the YofiScore type in order-schema.ts

export interface YofiIndicator {
  name?: string | null;
  is_risk?: boolean | null;
}

export interface YofiScore {
  prediction_name: string;
  prediction_value: YofiRiskLevels;
  justification?: string | null;
  indicators?: YofiIndicator[] | null;
}

export enum OrderTrackingBillingStatus {
  BILLED = "billed",
  FREE = "free",
}

export enum ProtectionSource {
  // coverage products that strictly match productIDs we have created
  VERIFIED_COVERAGE_PRODUCT = "verified_coverage_product",
  UNVERIFIED_COVERAGE_PRODUCT = "unverified_coverage_product",
  COVERAGE_IN_SHIPPING = "coverage_in_shipping",
  MERCHANT_PAID_PER_ORDER = "merchant_paid_per_order",
  // A previous order was protected within the pre-protected order window
  PRE_PROTECTED_ORDER = "pre_protected_order",
  // a protected order was exchanged for this order
  PROTECTED_EXCHANGE_ORDER = "protected_exchange_order",
  FINAL_SALE_COVERAGE = "final_sale_coverage",
  // a product-less checkout fee
  COVERAGE_CHECKOUT_FEE = "coverage_checkout_fee",
  BOLT_COVERAGE_CHECKOUT_FEE = "bolt_coverage_checkout_fee",
  FORCE_PROTECTED = "force_protected",
  CUSTOM_ORDER_SYNC_MARKED_PROTECTED = "custom_order_sync_marked_protected",
}

export interface Order extends ITrackable {
  coverage: Coverage;
  protected: boolean;
  redoPrice?: string;
  packageProtected: boolean;
  finalSaleReturnsProtected: boolean;
  credits: number;
  isExchangeOrder: boolean;
  coverageEnabled: boolean;
  _id: string;
  shopify_id: ProviderPrefixedOrderId;
  extendedWarranties: any[];
  team: string;
  createdAt: string;
  customer_name: string;
  lineItemsFulfillment: any[];
  finalSaleLineItemsProtection: FinalSaleLineItemProtection[];
  lineItems?: any[];
  stalledInFulfillment?: boolean;
  discount?: {
    id: string;
    description: string;
    code: string;
    expirationDateTime: string;
  };
  shopify: {
    id: OrderId;
    line_items: LineItem[];
    fulfillments: Fulfillment[];
    [key: string]: any;
    total_price: string;
    total_tax: string;
    financial_status: string;
    source_name: string;
    discount_codes: DiscountCode[];
    taxes_included: boolean;
    fulfillment_status: OrderFulfillmentStatus | null | undefined;
    cancelled_at: string | null | undefined;
    total_shipping_price_set: {
      shop_money: { amount: string };
      presentment_money: { amount: string };
    };
  };
  shopifyCreatedAt: string;
  shopifyUpdatedAt: string;
  timeline: TimelineEvent[];
  refundEvents?: OrderRefundEvent[];
  updatedAt: string;
  originItems?: OriginItem[];
  currentEmailFlows?: {
    emailFlowId: string;
    currentStep: number;
    continueDate: string;
    fulfillmentId?: string;
  }[];
  trackers: { _tracker: ExternalShipmentTracker; fulfillmentID: string }[];
  trackingAnalytics: {
    email: SesEvent[];
    page: {
      url: string;
      eventType: "ad" | "upsell" | "promotion" | "subscription";
      image?: string;
      createdAt: string;
    }[];
  };
  trackingTextsSent?: { sid: string; mms: boolean; sentAt: string }[];
  trackingEmailsSent?: {
    emailId: string;
    status?: string;
    sentAt: string;
    s3URL?: string;
    trigger?: string;
    trackingCode?: string;
  }[];
  trackingBillingStatus?: OrderTrackingBillingStatus;
  trackingBillingEligibleAt?: Date;
  attributedToRecoverAt?: Date;
  yofiScores?: Record<string, YofiScore>;
  shipmentRates?: ShipmentRatesOld;
  parcels?: Parcel[];
  labels?: { label: Label; rate: Rate; printed: boolean }[];
  provider?: Provider;
  conciergeAssistedDate?: Date;
  originOrder: MaybeOriginOrder;
  /** Redo shipping options chosen at checkout; carries customer-paid landed cost. */
  selectedRedoCheckoutShippingOptions?: CheckoutShippingOption[];
  tnplContract?: string;
  parentOrderId?: string;
  buyerId?: string;
  coverageAnalytics?: { redoOptedInFromCart?: boolean };
  commercecloud?: CommerceCloudOrder;
  commerceCloudLoyalty?: { tier?: string };
}

export interface OrderWithReturnableItems extends Order {
  hasReturnableItems?: boolean;
  line_items?: LineItem[];
}

export function getProviderDisplayName(provider: Provider): string {
  const map: Record<Provider, string> = {
    [Provider.COMMENTSOLD]: "CommentSold",
    [Provider.SHOPIFY]: "Shopify",
    [Provider.COMMERCE_CLOUD]: "Commerce Cloud",
    [Provider.BIGCOMMERCE]: "BigCommerce",
    [Provider.NETSUITE]: "NetSuite",
    [Provider.MANUAL]: "Manual",
    [Provider.TIKTOK]: "TikTok",
    [Provider.AMAZON]: "Amazon",
    [Provider.WALMART]: "Walmart",
    [Provider.EBAY]: "eBay",
    [Provider.APPAREL_MAGIC]: "Apparel Magic",
    [Provider.REDO_MARKETPLACE]: "Redo Marketplace",
    [Provider.SFOM]: "SFOM",
    [Provider.AIMS360]: "AIMS360",
    [Provider.CIN7_OMNI]: "Cin7 Omni",
    [Provider.CIN7_CORE]: "Cin7 Core",
    [Provider.REDO_API]: "Redo API",
    [Provider.CUSTOM_RETURN_INTEGRATION]: "Custom Return Integration",
    [Provider.REDO]: "Redo",
    [Provider.FAIRE]: "Faire",
    [Provider.WOOCOMMERCE]: "WooCommerce",
    [Provider.WAYFAIR]: "Wayfair",
    [Provider.BRIGHTPEARL]: "Brightpearl",
    [Provider.LINNWORKS_TRACKSTAR]: "Linnworks (Trackstar)",
    [Provider.LINNWORKS_DIRECT]: "Linnworks",
    [Provider.TEMU]: "Temu",
    [Provider.RITHUM]: "Rithum",
    [Provider.APTOS]: "Aptos",
  };
  return map[provider];
}

// Default source names for orders from different platforms. This is used for the coverage monitor, it should not be used for any other purpose. Use the Provider enum.
export enum DefaultOrderSource {
  SHOPIFY_WEB = "web",
  BIGCOMMERCE = "bigcommerce",
  COMMENTSOLD = "CommentSold",
  COMMERCECLOUD = "Commerce Cloud",
  SFOM = "Salesforce Order Management",
  CUSTOM_RETURN_INTEGRATION = "Custom Return Integration",
}

export const orderProviderToLabel: Record<Provider, string> = {
  [Provider.SHOPIFY]: "Shopify",
  [Provider.COMMENTSOLD]: "CommentSold",
  [Provider.COMMERCE_CLOUD]: "Commerce Cloud",
  [Provider.BIGCOMMERCE]: "BigCommerce",
  [Provider.MANUAL]: "Manual",
  [Provider.NETSUITE]: "NetSuite",
  [Provider.TIKTOK]: "TikTok",
  [Provider.AMAZON]: "Amazon",
  [Provider.WALMART]: "Walmart",
  [Provider.EBAY]: "eBay",
  [Provider.APPAREL_MAGIC]: "Apparel Magic",
  [Provider.REDO_MARKETPLACE]: "Redo Marketplace",
  [Provider.SFOM]: "SFOM",
  [Provider.AIMS360]: "AIMS360",
  [Provider.CIN7_OMNI]: "Cin7 Omni",
  [Provider.CIN7_CORE]: "Cin7 Core",
  [Provider.REDO_API]: "Redo API",
  [Provider.CUSTOM_RETURN_INTEGRATION]: "Custom Return Integration",
  [Provider.REDO]: "Redo",
  [Provider.FAIRE]: "Faire",
  [Provider.WOOCOMMERCE]: "WooCommerce",
  [Provider.WAYFAIR]: "Wayfair",
  [Provider.BRIGHTPEARL]: "Brightpearl",
  [Provider.LINNWORKS_TRACKSTAR]: "Linnworks (Trackstar)",
  [Provider.LINNWORKS_DIRECT]: "Linnworks",
  [Provider.TEMU]: "Temu",
  [Provider.RITHUM]: "Rithum",
  [Provider.APTOS]: "Aptos",
};

export enum OrderRefundEventType {
  SHIPPING = "shipping",
}

export interface OrdersFacets {
  total: number;
}

export interface TaxLine {
  price: string;
  rate: number;
  title: string;
}

export interface LineItem {
  id: number;
  product_id: number | null;
  sku: string;
  properties: { name: string; value: string }[];
  title: string;
  variant_title: string;
  variant_id: number;
  [key: string]: any;
  tax_lines: TaxLine[];
  discount_price: string | null;
  greenReturn?: boolean;
  inputAnswers?: InputAnswer[];
  merchantAddress?: any;
  multipleChoiceAnswers?: MultipleChoiceAnswer[];
  reason: string | null;
  selectedReturnType?: string;
  tags?: string[];
}

export interface OriginItem {
  unitPrice: string;
  preDiscountPrice?: string;
  currency: string;
  tax: string;
  originVariantId: number | string;
  newVariantId: number | string;
  applicableShopifyOrder?: number;
}

export interface Tracker {
  tracking_code: string;
  carrier: string;
  status: string;
  est_delivery_date: string;
  public_url?: string;
  created_at: string;
  updated_at: string;
  tracking_details: { message: string; status: string; datetime: string }[];
}

export interface Fulfillment {
  id: number;
  name: string;
  status: string;
  tracking_company: string | null;
  tracking_url: string | null;
  shipment_status: string | null;
  // If multiple tracking numbers are set on this fulfillment, only the first one will be returned in the tracking_number field.
  tracking_number: string;
  tracking_numbers: string[];
  tracking_urls: string[];
  line_items: LineItem[];
  created_at: string;
  updated_at?: string;
  location_id?: string;
  origin_address?: OriginAddress;
}

/**
 * Statuses where the carrier reports the package in the customer's hands.
 * Anything else (including null/unknown) means "not known to be delivered".
 */
export const DELIVERED_SHIPMENT_STATUSES: readonly string[] = [
  "delivered",
  "picked_up",
];

export function isShipmentStatusDelivered(
  shipmentStatus: string | null | undefined,
): boolean {
  return (
    !!shipmentStatus && DELIVERED_SHIPMENT_STATUSES.includes(shipmentStatus)
  );
}

// Only final sale protection is based on the line item, not the whole cart like
// package protection or returns coverage
export interface FinalSaleLineItemProtection {
  shopify_line_item_id: string;
  finalSaleReturnsProtected: boolean;
}

export function lineItemDiscountTotal(
  lineItem: Pick<ShopifyLineItem, "discount_allocations">,
): number {
  return (
    lineItem.discount_allocations?.reduce(
      (total: number, discount: { amount?: string | null }) =>
        total + Number(discount.amount ?? 0),
      0,
    ) || 0
  );
}

export function lineItemPriceAfterDiscounts(
  lineItem: Pick<
    ShopifyLineItem,
    "price" | "discount_price" | "discount_allocations"
  >,
): number {
  return Number(
    lineItem.discount_price ??
      Number(lineItem.price ?? 0) - lineItemDiscountTotal(lineItem),
  );
}

export function isLineItemFinalSaleProtected(
  lineItem: LineItem | ShopifyLineItem | undefined,
  finalSaleLineItemsProtection: FinalSaleLineItemProtection[] | undefined,
): boolean {
  const finalSaleLineItemProtection = finalSaleLineItemsProtection?.find(
    (protection) => protection.shopify_line_item_id === lineItem?.id.toString(),
  );
  return !!(
    lineItem?.finalSaleProtected ||
    (finalSaleLineItemProtection?.finalSaleReturnsProtected ?? false)
  );
}

export const DEFAULT_DUPLICATE_ORDER_NUMBER_FORMAT = " ({n})";

export class ExchangeOrderFormat {
  constructor(private readonly settings: ExchangeOrderNameSettings) {}

  private getAffixedString(
    original: string,
    prefix?: string,
    suffix?: string,
  ): string {
    return (prefix ?? "") + original + (suffix ?? "");
  }

  getName(original: string, prefixType: ExchangeOrderPrefixTypes) {
    if (this.settings.orderName.useShopifyGeneratedName) {
      return null;
    }

    // Strip # from original attribute
    // EX: Manly Bands Merchant
    if (this.settings.shouldStripHash) {
      original = original.replace("#", "");
    }

    if (isDefined(this.settings.suffix)) {
      return this.settings.suffix
        ? `${original}-${this.settings.suffix}`
        : original;
    }

    const typedOrderName = this.settings.orderName.orderNamesByReturnType.find(
      (typedOrderName) => typedOrderName.returnType === prefixType,
    );

    if (typedOrderName?.prefix || typedOrderName?.suffix) {
      return this.getAffixedString(
        original,
        typedOrderName?.prefix,
        typedOrderName?.suffix,
      );
    }

    return this.getAffixedString(
      original,
      this.settings.orderName.prefix,
      this.settings.orderName.suffix,
    );
  }

  /**
   * Formats the duplicate order number suffix.
   * @param baseName - The base order name (e.g., "ORDER-EXC")
   * @param count - The duplicate number (e.g., 2 for the second exchange), must be an integer >= 2
   * @returns The formatted name (e.g., "ORDER-EXC (2)" or "ORDER-EXC-2")
   */
  formatDuplicateNumber(baseName: string, count: number): string {
    if (count < 2 || !Number.isInteger(count)) {
      throw new Error("count must be an integer >= 2");
    }
    const format =
      this.settings.orderName.duplicateOrderNumberFormat ??
      DEFAULT_DUPLICATE_ORDER_NUMBER_FORMAT;
    return baseName + format.replace("{n}", String(count));
  }

  /**
   * Strips the duplicate number suffix from an order name.
   * Handles both default format " (n)" and custom formats like "-n".
   * @param name - The order name that may contain a duplicate suffix
   * @returns The name without the duplicate suffix
   */
  stripDuplicateNumber(name: string): string {
    const format =
      this.settings.orderName.duplicateOrderNumberFormat ??
      DEFAULT_DUPLICATE_ORDER_NUMBER_FORMAT;
    if (!format.includes("{n}")) {
      throw new Error(
        "duplicateOrderNumberFormat must contain {n} placeholder",
      );
    }
    // Build regex from the format by escaping special chars and replacing {n} with a number pattern
    const escapedFormat = format
      .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
      .replace(/\\\{n\\\}/g, "\\d+");
    const regex = new RegExp(`${escapedFormat}$`);
    return name.replace(regex, "");
  }

  /**
   * Computes the base exchange order name for a given original order name.
   * Preserves the original order name verbatim when it is a raw merchant
   * order. Only strips a duplicate counter suffix if the original name was
   * itself generated by our exchange naming logic (chained exchange).
   *
   * This avoids the class of bugs where legitimate "-digits" in an original
   * order name (e.g. Shopify order "SP-2") gets misread as a duplicate
   * counter and stripped before re-applying the exchange affix.
   */
  getBaseExchangeOrderName(
    original: string,
    prefixType: ExchangeOrderPrefixTypes,
    options?: { sourceOrderIsExchangeOrder?: boolean },
  ): string | null {
    if (this.settings.orderName.useShopifyGeneratedName) {
      return null;
    }

    if (options?.sourceOrderIsExchangeOrder) {
      const stripped = this.stripDuplicateNumber(original);
      // Accept the stripped form only if it is a legitimate exchange base —
      // that is, the inner content between prefix/suffix is non-empty AND
      // does not itself look like another exchange-affix application. This
      // prevents over-stripping in the "EXC-EXC-{counter}" pathology where
      // the counter is the team-wide duplicate counter and stripping it
      // collapses the name to the bare "EXC-EXC" prefix, which would then
      // re-collide against every other "EXC-EXC-*" team-wide.
      if (stripped !== original && this.isStrictExchangeBase(stripped)) {
        return stripped;
      }
      // Source is known to be a Redo-created exchange order, so its name
      // was already produced by our naming logic at some point — even if
      // the strict-check rejected the stripped form (e.g. legacy poisoning
      // produced a counter-suffixed shape like "EXC-EXC-343"). Preserve
      // verbatim instead of re-applying the prefix and double-wrapping.
      return original;
    }

    return this.getName(original, prefixType);
  }

  /**
   * Validates whether a name, produced by stripping a duplicate counter
   * from a source order name, is a legitimate exchange-formatted base.
   * Requires that, for at least one configured affix pair, the inner
   * content (between prefix and suffix) is non-empty AND does not collapse
   * to the same alphanumeric stem as the affix itself.
   *
   * Without this check, a name like "EXC-EXC-{counter}" (the pathology where
   * the prefix was accidentally double-applied and the counter ran against
   * the team-wide regex) would strip to "EXC-EXC" and be accepted as a
   * legitimate base — the next exchange would then strip back to "EXC-EXC"
   * again and append a fresh team-wide counter, perpetuating the bug.
   */
  private isStrictExchangeBase(name: string): boolean {
    const alnum = (s: string) => s.replace(/[^A-Za-z0-9]/g, "");

    if (isDefined(this.settings.suffix)) {
      const suffix = this.settings.suffix;
      if (!suffix) {
        return false;
      }
      const fullSuffix = `-${suffix}`;
      if (!name.endsWith(fullSuffix)) {
        return false;
      }
      const inner = name.slice(0, -fullSuffix.length);
      if (inner.length === 0) {
        return false;
      }
      // Reject if the inner content has the same alphanumeric stem as the
      // wrapper itself (i.e. "EXC-EXC" with suffix "-EXC" -> inner "EXC").
      if (alnum(inner) === alnum(suffix)) {
        return false;
      }
      return true;
    }

    const affixPairs: { prefix?: string; suffix?: string }[] = [
      {
        prefix: this.settings.orderName.prefix,
        suffix: this.settings.orderName.suffix,
      },
    ];
    for (const typedOrderName of this.settings.orderName
      .orderNamesByReturnType) {
      affixPairs.push({
        prefix: typedOrderName.prefix,
        suffix: typedOrderName.suffix,
      });
    }

    return affixPairs.some(({ prefix, suffix }) => {
      if (prefix && !name.startsWith(prefix)) {
        return false;
      }
      if (suffix && !name.endsWith(suffix)) {
        return false;
      }
      const inner = name.slice(
        prefix ? prefix.length : 0,
        suffix ? name.length - suffix.length : name.length,
      );
      if (inner.length === 0) {
        return false;
      }
      // No-affix config (reuse-original-order-number): the merchant has
      // opted into Redo naming but with empty prefix and suffix, so the
      // exchange name IS the original merchant order name (with collision
      // counters appended). There is no wrapper-on-wrapper pathology to
      // guard against, so accept any non-empty stripped form as a valid
      // base — this keeps chained collisions linear (e.g. "1000 (2)" ->
      // base "1000" -> next counter "1000 (3)") instead of nesting like
      // "1000 (2) (2)".
      if (!prefix && !suffix) {
        return true;
      }
      // Reject when the inner content collapses to the same alphanumeric
      // stem as the prefix/suffix. That signals the bare-wrapper pathology
      // (e.g. prefix "EXC-", name "EXC-EXC" -> inner "EXC") rather than a
      // real merchant-order base. Deeper poisoning like "EXC-EXC-1000" has
      // inner "EXC-1000" whose stem "EXC1000" differs from "EXC", so we
      // correctly accept "EXC-EXC-1000" as a legitimate (legacy) base.
      const innerStem = alnum(inner);
      if (prefix && innerStem === alnum(prefix)) {
        return false;
      }
      if (suffix && innerStem === alnum(suffix)) {
        return false;
      }
      return true;
    });
  }
}

export const getNumNonRedoItemsInOrders = (
  orders: Order[],
  redoCoverageProductIds: ReadonlySet<string>,
) => {
  // Exclude Redo items
  return orders.reduce((acc, order) => {
    return (
      acc +
      order.shopify.line_items
        ?.filter(
          (lineItem) => !isRedoLineItem(lineItem, redoCoverageProductIds),
        )
        .reduce((acc, lineItem) => acc + lineItem.quantity, 0)
    );
  }, 0);
};

const orderIdPrefix: Record<Provider, string> = {
  [Provider.SHOPIFY]: "",
  [Provider.COMMENTSOLD]: "cs:",
  [Provider.COMMERCE_CLOUD]: "",
  [Provider.BIGCOMMERCE]: "bc:",
  [Provider.NETSUITE]: "ns:",
  [Provider.MANUAL]: "",
  [Provider.AMAZON]: "",
  [Provider.TIKTOK]: "",
  [Provider.WALMART]: "",
  [Provider.EBAY]: "",
  [Provider.APPAREL_MAGIC]: "",
  [Provider.REDO_MARKETPLACE]: "",
  [Provider.SFOM]: "",
  [Provider.AIMS360]: "",
  [Provider.CIN7_OMNI]: "",
  [Provider.CIN7_CORE]: "",
  [Provider.REDO_API]: "API-",
  [Provider.CUSTOM_RETURN_INTEGRATION]: "",
  [Provider.REDO]: "redo:",
  [Provider.FAIRE]: "",
  [Provider.WOOCOMMERCE]: "wc:",
  [Provider.WAYFAIR]: "",
  [Provider.BRIGHTPEARL]: "",
  [Provider.LINNWORKS_TRACKSTAR]: "",
  [Provider.LINNWORKS_DIRECT]: "",
  [Provider.TEMU]: "",
  [Provider.RITHUM]: "",
  [Provider.APTOS]: "aptos:",
};

function isProviderPrefixedOrderId(
  orderId: OrderId,
  provider: Provider,
): boolean {
  return orderId.toString().startsWith(orderIdPrefix[provider]);
}

export function orderIdToProviderPrefixedOrderId(
  provider: Provider,
  orderId: OrderId,
): ProviderPrefixedOrderId {
  if (isProviderPrefixedOrderId(orderId, provider)) {
    return orderId as ProviderPrefixedOrderId;
  }
  return `${orderIdPrefix[provider]}${orderId}` as ProviderPrefixedOrderId;
}

export function providerPrefixedOrderIdToOrderId(
  provider: Provider,
  orderId: ProviderPrefixedOrderId,
): OrderId {
  if (!isProviderPrefixedOrderId(orderId, provider)) {
    return orderId as OrderId;
  }
  return orderId.replace(orderIdPrefix[provider], "") as OrderId;
}
